import { Button, IconButton, Modal } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { HiX } from "react-icons/hi";

interface WhitelistModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialWhitelist: string[];
  onSave: (whitelist: string[]) => void;
  onRemoveWord: (whitelist: string[]) => void;
}

export default function WhitelistModal({
  isOpen,
  onClose,
  initialWhitelist,
  onSave,
  onRemoveWord,
}: WhitelistModalProps) {
  const [whitelist, setWhitelist] = useState<string[]>(initialWhitelist);
  const [newWord, setNewWord] = useState("");

  useEffect(() => {
    setWhitelist(initialWhitelist);
  }, [initialWhitelist]);

  const handleAddWord = () => {
    const trimmed = newWord.trim();
    if (trimmed && !whitelist.includes(trimmed)) {
      setWhitelist([...whitelist, trimmed]);
      setNewWord("");
    }
  };

  const handleRemoveWord = (word: string) => {
    const newWhitelist = whitelist.filter((w) => w !== word);
    setWhitelist(newWhitelist);
    onRemoveWord(newWhitelist);
  };

  const handleSave = () => {
    onSave(whitelist);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddWord();
    }
  };

  return (
    <Modal title="Whitelist" isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">
          Add names or words that should be ignored during spell checking for this passage.
        </p>

        {/* Add new word input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a word to add..."
            className="flex-1 px-3 py-2 text-sm text-black border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <Button
            onClick={handleAddWord}
            disabled={!newWord.trim() || whitelist.includes(newWord.trim())}
            variant="secondary"
            className="shrink-0"
          >
            <FaPlus className="text-xs" />
          </Button>
        </div>

        {/* Whitelist display */}
        {whitelist.length > 0 ? (
          <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-md border border-slate-200 max-h-40 overflow-y-auto">
            {whitelist.map((word) => (
              <div
                key={word}
                className="flex items-center gap-1.5 px-2 py-1 bg-white border border-slate-200 rounded-md text-sm"
              >
                <span className="text-slate-700">{word}</span>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveWord(word)}
                  className="!p-0.5 hover:text-red-500"
                >
                  <HiX className="text-xs" />
                </IconButton>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-slate-400 bg-slate-50 rounded-md border border-dashed border-slate-300">
            No words in whitelist
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save Whitelist
          </Button>
        </div>
      </div>
    </Modal>
  );
}
